import { filePutSignedUrlBody } from "@/api/generated/schemas/api.zod"
import { customInstance } from "@/api/instance"

/** アップロードを受け付けるファイル種別。file input の accept 属性に渡す */
export const UPLOAD_ACCEPT = filePutSignedUrlBody.shape.contentType.options.join(",")

export type UploadResult = {
  key: string // S3のパス（エンティティ作成時に使用）
  fileName: string // 元のファイル名
}

type FilePutSignedUrlResponse = {
  signedUrl: {
    key: string
    url: string
  }
}

/**
 * ファイルアップロード用の署名付きURLを取得
 */
export async function filePutSignedUrl(params: {
  contentType: string
}): Promise<FilePutSignedUrlResponse> {
  return customInstance<FilePutSignedUrlResponse>("/files/put-signed-url", {
    method: "POST",
    body: JSON.stringify(params),
    headers: {
      "Content-Type": "application/json",
    },
  })
}

/**
 * ファイルをS3にアップロードする汎用関数
 * 1. 署名付きURL取得
 * 2. S3にPUTでアップロード
 */
export async function uploadFile(file: File): Promise<UploadResult> {
  // 1. 署名付きURL取得（許可外の種別はサーバーに送らず弾く）
  const body = filePutSignedUrlBody.safeParse({ contentType: file.type })
  if (!body.success) {
    throw new Error(`対応していないファイル形式です: ${file.name}`)
  }
  const { signedUrl } = await filePutSignedUrl(body.data)

  // 2. S3にアップロード
  const uploadResponse = await fetch(signedUrl.url, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type,
    },
  })

  if (!uploadResponse.ok) {
    throw new Error(`Upload failed: ${uploadResponse.status}`)
  }

  return {
    key: signedUrl.key,
    fileName: file.name,
  }
}

/**
 * 複数ファイルをアップロード
 */
export async function uploadFiles(files: File[]): Promise<UploadResult[]> {
  return Promise.all(files.map(uploadFile))
}

export const fileApi = {
  filePutSignedUrl,
  uploadFile,
  uploadFiles,
}
